import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Check, Clock } from "lucide-react";
import api from "@/lib/api";
import { Overline, FadeIn } from "@/components/site/Shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/services/${slug}`).then(({ data }) => setService(data)).catch(() => setService(false));
    api.get("/projects").then(({ data }) => setRelated(data.slice(0, 3))).catch(() => {});
  }, [slug]);

  if (service === null) return <div className="min-h-[60vh] flex items-center justify-center text-silver uppercase tracking-widest text-sm">Loading…</div>;
  if (service === false) return <div className="min-h-[60vh] flex items-center justify-center text-silver">Service not found.</div>;

  return (
    <div data-testid="service-detail-page">
      <section className="relative border-b border-line overflow-hidden">
        <div className="absolute inset-0">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-carbon via-carbon/85 to-carbon/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-36">
          <FadeIn>
            <Overline className="mb-5">OGK Service</Overline>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white leading-[0.95] max-w-3xl">{service.title}</h1>
            <p className="text-ember font-bold uppercase tracking-[0.2em] text-sm mt-5">{service.tagline}</p>
            <p className="text-silver max-w-2xl mt-6 leading-relaxed">{service.description}</p>
            <Link to="/request-quote" data-testid="service-quote-btn"
              className="inline-flex items-center gap-2 mt-10 bg-ember text-white uppercase text-sm tracking-[0.15em] font-bold px-8 py-4 hover:bg-[#e64a19] transition-colors duration-200">
              Request a Quote <ArrowUpRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <section className="py-24 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16">
          <FadeIn>
            <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-white mb-8">What You Gain</h2>
            <ul className="space-y-4">
              {service.benefits?.map((b) => (
                <li key={b} className="flex items-start gap-4 border border-line bg-graphite px-6 py-4 text-white/90 text-sm">
                  <Check size={17} className="text-ember shrink-0 mt-0.5" /> {b}
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-3 mt-8 text-silver text-sm border border-line bg-graphite px-6 py-4">
              <Clock size={17} className="text-ember" /> Typical timeline: <span className="text-white font-medium">{service.timeline}</span>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-white mb-8">Our Process</h2>
            <div className="space-y-0 border-l-2 border-line ml-3">
              {service.process?.map((p, i) => (
                <div key={p} className="relative pl-10 pb-9">
                  <span className="absolute -left-[15px] top-0 w-7 h-7 bg-carbon border-2 border-ember flex items-center justify-center text-ember font-heading text-xs font-bold">{i + 1}</span>
                  <p className="text-white/90 text-sm font-medium pt-1">{p}</p>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="py-24 border-b border-line bg-graphite">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-white mb-10">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="service-faqs">
            {service.faqs?.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-line bg-carbon px-6">
                <AccordionTrigger className="text-white text-left text-sm font-medium hover:no-underline hover:text-ember">{f.q}</AccordionTrigger>
                <AccordionContent className="text-silver text-sm leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-white mb-10">Related Projects</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {related.map((p) => (
              <Link key={p.slug} to={`/projects/${p.slug}`} className="group img-zoom relative overflow-hidden border border-line block h-64">
                <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <p className="text-ember text-xs uppercase tracking-[0.2em] font-bold">{p.category}</p>
                  <h3 className="font-heading text-lg text-white uppercase mt-1">{p.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
